import styled from 'styled-components';
import img2 from './images/skotheld.png';
import img1 from './images/enn530783.png';
import draugaimg from './images/draugamandarinur.jpg';
import { MobileWorks } from './mobileView';
import { useIsMobile } from '../hooks/use-is-mobile';
import {
  breakpoints,
  colors,
  Divider,
  DividerDiamond,
  fonts,
  FramedImage,
  Page,
  PullQuote,
  SectionTitle,
} from '../styles/theme';

const BookHero = styled.section`
  display: grid;
  grid-template-columns: minmax(240px, 320px) 1fr;
  gap: 64px;
  align-items: start;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 40px;
    max-width: 640px;
    margin: 0 auto;
  }
`;

const CoverColumn = styled.div`
  position: sticky;
  top: 110px;

  @media (max-width: 900px) {
    position: static;
    max-width: 280px;
    margin: 0 auto;
    width: 100%;
  }
`;

const BookContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  font-size: 16px;
  line-height: 1.8;
  text-align: justify;
`;

const BookTitle = styled.h1`
  font-size: 40px;
  line-height: 1.15;
  color: ${colors.accent};
  letter-spacing: 0.02em;

  @media (max-width: ${breakpoints.mobile}) {
    font-size: 30px;
  }
`;

const TitleRule = styled.div`
  width: 64px;
  height: 2px;
  background: ${colors.gold};
`;

const BookAward = styled.p`
  font-family: ${fonts.display};
  font-weight: 600;
  font-size: 17px;
  color: ${colors.gold};
  letter-spacing: 0.04em;
`;

const PressLinks = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  margin-top: 10px;
`;

const PressLink = styled.a`
  display: inline-flex;
  align-items: center;
  padding: 12px 22px;
  border: 1px solid ${colors.accent};
  color: ${colors.accent};
  text-decoration: none;
  font-size: 12.5px;
  font-weight: 500;
  letter-spacing: 0.14em;
  transition: background 0.25s ease, color 0.25s ease;

  &:hover {
    background: ${colors.accent};
    color: ${colors.cream};
  }
`;

const SectionDivider = styled.div`
  margin: 84px 0;

  @media (max-width: ${breakpoints.mobile}) {
    margin: 56px 0;
  }
`;

const AwardsGrid = styled.section`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 32px;

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
    max-width: 640px;
    margin: 0 auto;
  }
`;

const AwardCard = styled.div`
  background: ${colors.surface};
  border: 1px solid ${colors.line};
  border-top: 3px solid ${colors.gold};
  padding: 40px 36px;
  font-size: 15.5px;
  line-height: 1.8;
  text-align: justify;
  box-shadow: 0 12px 30px rgba(42, 30, 21, 0.07);
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const AwardTitle = styled.p`
  font-weight: 600;
  font-size: 17px;
  color: ${colors.accent};
  font-family: ${fonts.display};
`;

const JuryLabel = styled.p`
  font-size: 12.5px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${colors.inkSoft};
`;

const SkotheldSection = styled.section`
  background: ${colors.cream};
  border: 1px solid ${colors.line};
  padding: 56px;
  display: grid;
  grid-template-columns: 1fr minmax(180px, 220px);
  gap: 56px;
  align-items: center;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    padding: 40px 28px;
    gap: 36px;
    max-width: 640px;
    margin: 0 auto;
  }
`;

const SkotheldText = styled.div`
  font-size: 16px;
  line-height: 1.8;
  text-align: justify;
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const SkotheldImageFrame = styled(FramedImage)`
  max-width: 220px;
  width: 100%;

  @media (max-width: 900px) {
    max-width: 180px;
    margin: 0 auto;
  }
`;

export function Books() {
  const isMobile = useIsMobile();

  if (isMobile) {
    return <MobileWorks />;
  }

  return (
    <Page>
      <BookHero>
        <CoverColumn>
          <FramedImage>
            <img src={draugaimg} alt="Draugamandarínur" />
          </FramedImage>
        </CoverColumn>
        <BookContent>
          <BookTitle>DRAUGAMANDARÍNUR (2025)</BookTitle>
          <TitleRule />
          <BookAward>Hlaut bóksalaverðlaunin 2025</BookAward>
          <p>Gefin út af Drápu bókaútgáfu, fáanleg í helstu bókabúðum landsins.</p>
          <p>
            Hýðinu er flett af ávextinum. Fyrir innan leynist ýmislegt: matur, myrkur,
            minningar. Fyrir utan: vökult auga.
            Draugamandarínur fjallar um hvað það merkir að gefa af sér. Verkið skoðar, í
            gegnum hrylling, smáatriði og rytma, þá athöfn sem fer fram þegar matar er
            neytt. Líkaminn skipar mikilvægan sess í textanum, horft er inn á við eins og
            í gegnum röntgengeisla og þar má meðal annars sjá sársauka, hungur og
            aldinkjöt.
          </p>
          <PullQuote>
            „Í Draugamandarínum beitir höfundur ljóðlistinni til þess að spretta
            tilverunni upp, stinga fingrinum inn og þreifa eftir kjarnanum. Hér er hlutum
            snúið á rönguna, innvolsið skafið út og holdið gegnumlýst svo beinin standa
            eftir ein og sér. (…) Ljóðin eru líkamleg, leikræn og safarík og rata beinustu
            leið undir skinnið.“
            <footer>- Snædís Björnsdóttir, Morgunblaðinu</footer>
          </PullQuote>
        </BookContent>
      </BookHero>

      <SectionDivider>
        <Divider>
          <DividerDiamond />
        </Divider>
      </SectionDivider>

      <BookHero>
        <CoverColumn>
          <FramedImage>
            <img src={img1} alt="Moldin heit" />
          </FramedImage>
        </CoverColumn>
        <BookContent>
          <BookTitle>MOLDIN HEIT (2024)</BookTitle>
          <TitleRule />
          <p>Moldin heit er skáldsaga um ástina, listina, sorg og missi.</p>
          <p>
            Ástin í lífi Karenar deyr. Hún mætir í jarðarförina óboðin, situr aftast og segir engum hver hún er. En eftir að hafa borið eina fortíð til grafa skýtur önnur upp kollinum. Hún sér Ými, kollega sinn, út um gluggann í erfidrykkjunni þar sem hann virðist ætla að fara sér að voða.
            Í kjölfarið er þeim hrundið af stað í ferð þar sem þau þurfa að horfast í augu við hvaða hlutverk þau leiki hvort í annars lífi og í flóknu dansverki, og hvaða nýju hlutverk standa þeim til boða.
          </p>
          <PullQuote>
            „Líkaminn er stórt og fyrirferðarmikið þema í verkinu, þarna koma fyrir ósjálfráða líkamar, líkamar á hreyfingu, líkamar sem frjósa og því er lýst hvernig tónlist skekur líkamann…”
            <footer>- Díana Sjöfn, Lestrarklefanum</footer>
          </PullQuote>
          <PressLinks>
            <PressLink href="https://lestrarklefinn.is/2024/11/15/eins-konar-dans/">LESTRARKLEFINN</PressLink>
            <PressLink href="https://www.ruv.is/frettir/menning-og-daegurmal/2024-10-28-traustid-brestur-thegar-listamenn-thurfa-ad-keyra-sig-afram-i-vondum-adstaedum-425650?fbclid=IwY2xjawGwJxZleHRuA2FlbQIxMQABHXtCJqwxlPZ8Tio0ZZA460jl86NS-TtvKE56jav4lBDOTXXCfRv_Hw0NXQ_aem_UymHY9XOolJXwU2wTvG-RA">MENNINGIN</PressLink>
            <PressLink href="https://www.ruv.is/utvarp/spila/lestin/23619/7hr9c6?fbclid=IwY2xjawGwJ0dleHRuA2FlbQIxMQABHcEsWzaD1t2t8lLVuETSNvXzc8bgAjia8vnjUvuW8lae_kDhSXQyBM8n_A_aem_U2HhEpBVNEq-O37XLs8jzQ">LESTIN</PressLink>
            <PressLink href="https://www.ruv.is/sjonvarp/spila/kiljan/37024/b12qg6">KILJAN</PressLink>
          </PressLinks>
        </BookContent>
      </BookHero>

      <SectionDivider>

      </SectionDivider>

      <AwardsGrid>
        <AwardCard>
          <AwardTitle>Hlaut Fjöruverðlaunin 2025.</AwardTitle>
          <JuryLabel>Rökstuðningur dómnefndar:</JuryLabel>
          <p>
            Listir, náttúra og ólgandi tilfinningar takast á í bókinni Moldin heit eftir Birgittu Björgu Guðmarsdóttur.
            Hrynjandi ljóðræns textans knýr framvindu áfram þar sem meginstef eru ást, missir og innri leit. Form texta og litanotkun ljær frásögninni dýpt
            og er lesanda gefið rými til að skynja og túlka atburðarás sem lýkst upp eftir því sem líður á og rís hæst í sólódansi aðalpersónunnar.
            Birgitta Björg slær hér nýjan og forvitnilegan bókmenntatón.
          </p>
        </AwardCard>
        <AwardCard>
          <AwardTitle>Tilnefnd til Íslensku bókmenntaverðlaunanna 2025.</AwardTitle>
          <JuryLabel>Rökstuðningur dómnefndar:</JuryLabel>
          <p>
            Með léttum og hrífandi ritma er lesendum boðið upp í dans.
            Smám saman brýst út tilfinningaþrungin frásögn í listilega uppbyggðri skáldsögu.
            Listrænn en jafnframt aðgengilegur texti sem ætti að höfða til allra.
          </p>
        </AwardCard>
      </AwardsGrid>

      <SectionDivider>
        <Divider>
          <DividerDiamond />
        </Divider>
      </SectionDivider>

      <SkotheldSection>
        <SkotheldText>
          <SectionTitle>SKOTHELD (2018)</SectionTitle>
          <p>
            Skotheld segir frá ungu fólki í þorpi þar sem leyndarmál grúfa undir.
          </p>
          <p>
            Freyja vill komast hærra í metorðastiga þorpsins. Hún vingast við Agnesi sem hefur virðist hafa óflekkaða ímynd. Hægt og rólega sölsar Freyja undir sig stærri og stærri hlut af lífi Agnesar, starf hennar, kærasta og loks ímynd. En líf Agnesar er ekki dans á rósum, eins og Freyja kemst fljótt að.
          </p>
          <p>
            Grímur er sonur fyrrum hetju bæjarins og heyrir aldrei neitt slæmt sagt um föður sinn. Þar til einn daginn heyrir hann nokkuð misjafnt sem kemur honum úr jafnvægi og sendir hann í leit að sannleikanum um foreldra sína.
          </p>
        </SkotheldText>
        <SkotheldImageFrame>
          <img src={img2} alt="Skotheld" />
        </SkotheldImageFrame>
      </SkotheldSection>
    </Page>
  );
}
